// The person's circles (36-desyr-id.md, Behaviour 2 and 6): the admin service's list is the truth.
// Every circle's session comes from one call (v1.58.15); a circle that needs a hop, or every circle
// when the call isn't there, signs in silently, one after the other, by same-window hops whose queue
// survives the trips (`desyr_id_queue`). A circle is "ready" once its account is on this device.
import { useSyncExternalStore } from 'react';
import { switchTo } from '../accounts/store';
import { toast } from '../blocks/Toast';
import { locale, t } from '../i18n';
import type { Config, DesyrIdConfig } from '../core/config';
import { readSession } from '../core/session';
import { waiting } from '../accounts/store';
import { circleLogo, myCircles, type Circle, type CirclesAnswer } from './api';
import { hop, hopTimedOut, runUnderWay } from './hop';
import { callSessions, unknownCircles, type Given, type Outcome } from './sessions';
import { keepQueue, leaving, readQueue, takeAfter } from './store';

export type CircleState = 'signing-in' | 'ready' | 'failed' | 'passkey';
interface Circles {
  answer?: CirclesAnswer;
  /** The list couldn't be loaded (the admin service doesn't answer). */
  unreachable?: boolean;
  /** Circles (server names) whose silent sign-in failed. */
  failed: string[];
  /** Logos by circle slug, as local addresses (fetched with the ID token, shown from memory). */
  logos: Record<string, string>;
  /** Each circle's state in the last sessions call of this page (server name → state); none in the fallback. */
  given?: Record<string, Given>;
}
let state: Circles = { failed: [], logos: {} };
const listeners = new Set<() => void>();
const set = (next: Partial<Circles>) => ((state = { ...state, ...next }), listeners.forEach((l) => l()));
const subscribe = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));
export const useCircles = () => useSyncExternalStore(subscribe, () => state);
export const circlesNow = () => state;

/** The circle's account is on this device (open, or waiting and not signed out). */
export const onDevice = (mxid: string) => readSession()?.user_id === mxid || waiting().some((a) => a.user_id === mxid && !a.signed_out);

export function stateOf(circle: Circle): CircleState {
  if (onDevice(circle.mxid)) return 'ready';
  const given = state.given?.[circle.server];
  if (given === 'passkey') return 'passkey';
  if (given && given !== 'hop') return 'failed';
  return state.failed.includes(circle.server) || hopTimedOut(circle.server) || !readQueue().includes(circle.server) ? 'failed' : 'signing-in';
}

/** The list (with the invitations and the email's state); undefined when it can't be loaded. */
export async function loadCircles(config: DesyrIdConfig): Promise<CirclesAnswer | undefined> {
  try {
    return take(config, await myCircles(config));
  } catch {
    set({ unreachable: true });
    return undefined;
  }
}

/** A list (from `GET /me/circles` or the sessions call) kept, the circles being left out, the logos fetched. */
function take<A extends CirclesAnswer>(config: DesyrIdConfig, answer: A, given?: Record<string, Given>): A {
  // A circle being left isn't shown or signed in while the list still holds it (39, Behaviour 1.6).
  const left = leaving(answer.circles.map((c) => c.server));
  answer.circles = answer.circles.filter((c) => !left.includes(c.server));
  set({ answer, unreachable: false, ...(given && { given }) });
  for (const c of [...answer.circles, ...(answer.invitations ?? []).map((i) => i.circle)])
    if (c.pictureUrl && !state.logos[c.slug]) void circleLogo(config, c.pictureUrl).then((url) => url && set({ logos: { ...state.logos, [c.slug]: url } }));
  return answer;
}

/**
 * The sessions call (36, Behaviour 6), its list and states kept. Accounts here from a hop or the old
 * sign-in that don't know their circle yet: the list first, once.
 */
let lastCall = 0;
export async function syncCircles(config: Config): Promise<Outcome> {
  const id = config.desyrId!;
  lastCall = Date.now();
  if (unknownCircles() && !state.answer) await loadCircles(id);
  const outcome = await callSessions(config, { slugs: (mxid) => state.answer?.circles.find((c) => c.mxid === mxid)?.slug });
  if ('answer' in outcome) take(id, outcome.answer, Object.fromEntries(outcome.answer.circles.map((c) => [c.server, c.session?.state ?? 'hop'])));
  // Without the call, the list as before (Behaviour 2.1): the circles gone from it still go quietly.
  if ('fallback' in outcome) await loadCircles(id);
  return outcome;
}

/** The list a call came to: its answer, or in the fallback `GET /me/circles`' (none when that failed too). */
export const listAfter = (outcome: Outcome | undefined): CirclesAnswer | undefined =>
  !outcome ? undefined : 'answer' in outcome ? outcome.answer : 'fallback' in outcome && !state.unreachable ? state.answer : undefined;

/**
 * Right after the desyr ID sign-in ("Opening your circles…", v1.58.15): desyr opens at once when
 * every circle came back ready (and no invitation waits); "Your circles" when something needs the
 * person or the fallback runs; the sign-in card's error when desyr ID can't be reached.
 */
export async function openAfterSignIn(config: Config): Promise<'open' | 'circles' | 'idUnavailable' | 'failed'> {
  const outcome = await syncCircles(config);
  if ('error' in outcome) return outcome.error === 'unavailable' ? 'idUnavailable' : 'failed';
  if (!('answer' in outcome)) return 'circles';
  const { circles, invitations } = outcome.answer;
  return circles.length && circles.every((c) => onDevice(c.mxid)) && !invitations?.length ? 'open' : 'circles';
}

/**
 * "Your circles" (36, Screens): after the call, the hops of the circles that need one (Behaviour
 * 6.5); a run under way goes on with what is left of it. Without the call, the list and every hop.
 */
export async function startCircles(config: Config): Promise<void> {
  const outcome = state.given ? undefined : await syncCircles(config);
  if (outcome && 'dropped' in outcome) return; // signed out meanwhile: no hop (36, Behaviour 4.4.3)
  if (outcome && 'error' in outcome) return void set({ unreachable: true });
  if (outcome && 'fallback' in outcome) return void (listAfter(outcome) && (await continueCircles(config)));
  const hops = (state.answer?.circles ?? []).filter((c) => state.given?.[c.server] === 'hop' && !onDevice(c.mxid)).map((c) => c.server);
  keepQueue(readQueue().length || runUnderWay() ? readQueue().filter((s) => hops.includes(s)) : hops);
  return signInNext(config);
}

/**
 * While desyr is open (36, Behaviour 6.6): the call every 5 minutes and when the window gains focus
 * (at most once a minute; the start's own call counts), renewing what is due; `then` gets each list.
 * A renewal nobody asked for that is refused never leaves the page: the circles keep their sessions
 * until they end, and the next action that needs desyr ID signs in again (Behaviour 1.4, v1.58.66).
 */
export function whileOpen(config: Config, then: (answer: CirclesAnswer) => void): () => void {
  const run = (force: boolean) => {
    if (!force && Date.now() - lastCall < 60_000) return void (state.answer && then(state.answer));
    void syncCircles(config).then((outcome) => {
      const list = listAfter(outcome);
      if (list) then(list);
    });
  };
  run(false);
  const timer = setInterval(() => run(true), 5 * 60_000);
  const focus = () => run(false);
  addEventListener('focus', focus);
  return () => (clearInterval(timer), removeEventListener('focus', focus));
}

/** "Retry" for one circle: the call again for one that couldn't be reached or was ended, else its hop. */
export const retry = (config: Config, circle: Circle) =>
  state.given?.[circle.server] && state.given[circle.server] !== 'hop' ? syncCircles(config).then(() => {}) : retryCircle(config, circle.server);

/** The next circle in the queue signs in; one that fails is marked and the queue goes on. */
export async function signInNext(config: Config, joined?: string): Promise<void> {
  const circles = state.answer?.circles ?? [];
  for (const server of readQueue()) {
    const circle = circles.find((c) => c.server === server);
    if (circle && !onDevice(circle.mxid)) {
      try {
        return await hop(config, circle.server, circle.server === joined);
      } catch {
        set({ failed: [...state.failed.filter((s) => s !== server), server] });
      }
    }
    keepQueue(readQueue().filter((s) => s !== server));
  }
}

/** Every listed circle not on this device, in the list's order. */
/** `joined`: the circle a Join just accepted (its hop may continue through the account service's page). */
export function signInCircles(config: Config, joined?: string): Promise<void> {
  keepQueue((state.answer?.circles ?? []).filter((c) => !onDevice(c.mxid)).map((c) => c.server));
  return signInNext(config, joined);
}

/** At a start (36, Behaviour 2.2): the run under way goes on; a new one only when none is. */
export const continueCircles = (config: Config) => (readQueue().length || runUnderWay() ? signInNext(config) : signInCircles(config));

/** "Retry" for one circle. */
export function retryCircle(config: Config, server: string): Promise<void> {
  set({ failed: state.failed.filter((s) => s !== server) });
  keepQueue([server, ...readQueue().filter((s) => s !== server)]);
  return signInNext(config);
}

/** A circle being left (39): remembered, and gone from the list at once. */
export function leftCircle(server: string): void {
  leaving(undefined, server);
  if (state.answer) set({ answer: { ...state.answer, circles: state.answer.circles.filter((c) => c.server !== server) } });
}

/** The circles on this device (`here`, chat IDs) that the list no longer holds (36, Behaviour 2.3; 39, Behaviour 2), in that order. */
export const gone = (circles: Circle[], here: string[]) => here.filter((u) => !circles.some((c) => c.mxid === u));

/** The toast kept over a reload (39, Behaviour 1.5 and 2.2), shown once. */
export function noteAfter(): void {
  const after = takeAfter('left', 'removed', 'joined');
  if (after?.do === 'joined') toast(t('id.joined', { circle: after.name }), { label: t('id.openCircle'), run: () => switchTo(after.mxid, '/', after.name) });
  else if (after) toast(t(after.do === 'left' ? 'id.leavingToast' : 'id.removedToast', { circle: after.name }));
}

/** Names as a list in the active language: "A, B and C". */
export const listOf = (names: string[]) => new Intl.ListFormat(locale(), { type: 'conjunction' }).format(names);
